import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, Input, Spin, Result } from "antd";
import LeftContent from "./leftContent";
import { Link, useLocation } from "react-router-dom";
import logoText from "@assets/images/logo/dark-logo-text-nobg.png";
import {
  validateTeamInvite,
  acceptTeamInvite,
  acceptTeamInviteExistingUser,
} from "@api/user/admin";
import { SuccessNotification, ErrorNotification } from "@components";

const initialData = {
  name: "",
  password: "",
  confirmPassword: "",
};

// Error configuration for different error types
const errorConfig = {
  expired: {
    title: "Invitation Expired",
    message: "This invitation link has expired. Please ask your team administrator to send a new invitation.",
  },
  revoked: {
    title: "Invitation Revoked",
    message: "This invitation has been revoked by the team administrator.",
  },
  invalid: {
    title: "Invalid Invitation",
    message: "This invitation link is invalid or incomplete. Please check your email for the correct link.",
  },
  already_in_team: {
    title: "Already in a Team",
    message: "You are already part of a team. Please leave your current team before joining a new one.",
  },
};

const TeamInviteAccept = () => {
  const [values, setValues] = useState(initialData);
  const [success, setSuccess] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [invitationError, setInvitationError] = useState(null);

  const search = useLocation().search;
  const token = new URLSearchParams(search).get("token");

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setInvitationError({
          type: "invalid",
          ...errorConfig.invalid,
        });
        setIsValidating(false);
        return;
      }

      try {
        const response = await validateTeamInvite(token);
        setInviteData(response.data);
        setIsValidating(false);
      } catch (err) {
        const errorType = err.response?.data?.type || "invalid";
        setInvitationError({
          type: errorType,
          ...(errorConfig[errorType] || errorConfig.invalid),
          serverMessage: err.response?.data?.message,
        });
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Handle form change
  const handleFormChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  // Handle new user submit (needs name and password)
  const handleNewUserSubmit = (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    const { name, password, confirmPassword } = values;

    // Validate all fields
    if (!name || !password || !confirmPassword) {
      ErrorNotification("Please fill all fields");
      setSubmitLoading(false);
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      ErrorNotification("Passwords don't match");
      setSubmitLoading(false);
      return;
    }

    // Password validation
    if (password.length < 8) {
      ErrorNotification("Password must be at least 8 characters long");
      setSubmitLoading(false);
      return;
    }

    acceptTeamInvite({ token, name, password })
      .then(() => {
        SuccessNotification("Successful! Log in to continue");
        setSubmitLoading(false);
        setSuccess(true);
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.type?.[0]?.message ||
          err.response?.data?.message ||
          "Failed to join team";
        ErrorNotification(errorMessage);
        setSubmitLoading(false);
        console.log("TEAM_ACCEPT_ERROR", err);
      });
  };

  // Handle existing user confirm (no password needed)
  const handleExistingUserConfirm = () => {
    setSubmitLoading(true);

    acceptTeamInviteExistingUser(token)
      .then(() => {
        SuccessNotification("Successful! Log in to continue");
        setSubmitLoading(false);
        setSuccess(true);
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.message || "Failed to join team";
        ErrorNotification(errorMessage);
        setSubmitLoading(false);
        console.log("TEAM_ACCEPT_ERROR", err);
      });
  };

  // Loading state
  if (isValidating) {
    return (
      <Row
        gutter={[32, 0]}
        className="hp-authentication-page"
        style={{ overflowY: "auto", height: "100vh" }}
      >
        <LeftContent />
        <Col lg={8} md={16} sm={16} span={24}>
          <Row className="hp-h-100" align="middle" justify="center">
            <Col>
              <Spin size="large" />
              <p style={{ marginTop: 16, textAlign: "center" }}>
                Validating invitation...
              </p>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  // Error state
  if (invitationError) {
    return (
      <Row
        gutter={[32, 0]}
        className="hp-authentication-page"
        style={{ overflowY: "auto", height: "100vh" }}
      >
        <LeftContent />
        <Col lg={8} md={16} sm={16} span={24}>
          <Row className="hp-h-100" align="middle" justify="center">
            <Col xxl={11} xl={15} lg={20} md={16} sm={24} className="hp-px-sm-8">
              <img
                src={logoText}
                alt="logo"
                style={{
                  maxWidth: "200px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto 24px",
                }}
              />
              <Result
                status={
                  invitationError.type === "revoked" ? "warning" : "error"
                }
                title={invitationError.title}
                subTitle={
                  invitationError.serverMessage || invitationError.message
                }
                extra={[
                  <Link to="/login" key="login">
                    <Button type="primary">Go to Login</Button>
                  </Link>,
                  <Link to="/" key="home">
                    <Button>Go to Home</Button>
                  </Link>,
                ]}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row
        gutter={[32, 0]}
        className="hp-authentication-page"
        style={{ overflowY: "auto", height: "100vh" }}
      >
        <LeftContent />

        <Col
          lg={8}
          md={16}
          sm={16}
          span={24}
          className="hp-py-sm-0 hp-py-md-0"
          justify="center"
        >
          <Row className="hp-h-100" align="middle" justify="center">
            <Col
              xxl={11}
              xl={15}
              lg={20}
              md={16}
              sm={24}
              className="hp-px-sm-8 hp-pt-24 hp-pb-48"
            >
              <img
                src={logoText}
                alt="logo"
                style={{
                  maxWidth: "200px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto",
                }}
              />

              {!success && (
                <>
                  <h2
                    className="hp-mb-sm-0"
                    style={{
                      textAlign: "center",
                      fontSize: "24px",
                      lineHeight: "32px",
                      color: "rgba(0, 0, 0, 0.85)",
                      marginTop: "16px",
                    }}
                  >
                    Join Team
                  </h2>
                  {inviteData?.teamName && (
                    <p
                      style={{
                        textAlign: "center",
                        color: "rgba(0, 0, 0, 0.45)",
                        marginBottom: "24px",
                      }}
                    >
                      You've been invited to join{" "}
                      <strong>{inviteData.teamName}'s</strong> team
                    </p>
                  )}
                </>
              )}

              {/* New user form */}
              {!success && inviteData && !inviteData.isExistingUser ? (
                <>
                  <Form
                    layout="vertical"
                    name="basic"
                    className="hp-mt-sm-16 hp-mt-32"
                  >
                    {/* Email (readonly) */}
                    <Form.Item className="hp-mb-16" label="Email">
                      <Input value={inviteData.email} disabled />
                    </Form.Item>

                    {/* Name */}
                    <Form.Item
                      className="hp-mb-16"
                      label="Name"
                      rules={[
                        { required: true, message: "Please enter your name" },
                      ]}
                    >
                      <Input
                        id="validating"
                        name="name"
                        placeholder="Enter your name"
                        value={values.name}
                        onChange={handleFormChange}
                      />
                    </Form.Item>

                    {/* Password */}
                    <Form.Item
                      className="hp-mb-16"
                      label="Password"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your password",
                        },
                      ]}
                    >
                      <Input.Password
                        id="warning2"
                        name="password"
                        value={values.password}
                        placeholder="Enter password"
                        onChange={handleFormChange}
                      />
                    </Form.Item>

                    {/* Confirm Password */}
                    <Form.Item
                      className="hp-mb-16"
                      label="Confirm Password"
                      rules={[
                        {
                          required: true,
                          message: "Please confirm your password",
                        },
                      ]}
                    >
                      <Input.Password
                        id="warning2"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        placeholder="Confirm password"
                        onChange={handleFormChange}
                      />
                    </Form.Item>

                    {/* Submit Button */}
                    <Form.Item className="hp-mt-16">
                      <Button
                        block
                        type="primary"
                        htmlType="submit"
                        onClick={handleNewUserSubmit}
                        loading={submitLoading}
                      >
                        Join Team
                      </Button>
                    </Form.Item>
                  </Form>

                  <div className="login-create-acc">
                    <p>Already have an account? </p>
                    <Link to="/login"> Login</Link>
                  </div>
                </>
              ) : !success && inviteData && inviteData.isExistingUser ? (
                /* Existing user confirmation */
                <Col className="hp-form-info" style={{ textAlign: "center" }}>
                  <p style={{ marginBottom: "8px", color: "rgba(0, 0, 0, 0.45)" }}>
                    Joining as
                  </p>
                  <h3 style={{ marginBottom: "4px" }}>{inviteData.name}</h3>
                  <p style={{ color: "rgba(0, 0, 0, 0.45)", marginBottom: "24px" }}>
                    {inviteData.email}
                  </p>

                  <Button
                    block
                    type="primary"
                    size="large"
                    onClick={handleExistingUserConfirm}
                    loading={submitLoading}
                  >
                    Confirm & Join Team
                  </Button>

                  <div
                    style={{ marginTop: "16px", color: "rgba(0, 0, 0, 0.45)" }}
                  >
                    <p>Not you?</p>
                    <Link to="/login">Login with a different account</Link>
                  </div>
                </Col>
              ) : (
                /* Success state */
                <Col className="hp-form-info" style={{ textAlign: "center" }}>
                  <Result
                    status="success"
                    title="Joined Successfully!"
                    subTitle="You have successfully joined the team. You can now login to access your account."
                    extra={[
                      <Link to="/login" key="login">
                        <Button type="primary" size="large">
                          Go to Login
                        </Button>
                      </Link>,
                    ]}
                  />
                </Col>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default TeamInviteAccept;
